import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { X, Calendar, User, MapPin, Package, DollarSign, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Order } from '../types';
import { useCurrencyFormat } from '../hooks/useCurrencyFormat';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { ordersService } from '../services/ordersService';
import CustomerReferenceField from '../visitors/CustomerReferenceField';

interface ProductSummaryDialogProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
}

// Product cache to avoid repeated API calls
const productCache = new Map();

const ProductNameDisplay = ({ productId }: { productId: string | number }) => {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(!productCache.has(productId));
  const [product, setProduct] = useState(productCache.get(productId));
  const [error, setError] = useState<Error | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    // Cleanup function to set isMounted to false when component unmounts
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!productId) return;

    // If we have it in cache, use it
    if (productCache.has(productId)) {
      setProduct(productCache.get(productId));
      setLoading(false);
      return;
    }

    // Otherwise fetch it
    setLoading(true);
    setError(null);

    // Use authenticatedFetch to fetch product data
    authenticatedFetch(`/api/products/${productId}`)
      .then((response) => response.json())
      .then((productData) => {
        if (isMounted.current) {
          // Store in cache
          productCache.set(productId, productData);
          setProduct(productData);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted.current) {
          console.error('Error loading product:', err);
          setError(err);
          setLoading(false);
        }
      });
  }, [productId]);

  if (loading) {
    return (
      <div className="text-sm text-gray-600 mt-1">
        <span className="inline-block animate-pulse">Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 mt-1">Error loading product</div>
    );
  }

  return (
    <div className="text-sm text-gray-600 mt-1 space-y-1">
      <div>
        <span className="font-medium text-gray-900">
          {product?.name || `Product ID: ${productId}`}
        </span>
        {product?.reference && (
          <span className="text-gray-500 ml-2">({product.reference})</span>
        )}
      </div>
      {product?.categoryId && (
        <div className="text-xs text-gray-500">
          Category ID: {product.categoryId}
        </div>
      )}
      {product?.description && (
        <div className="text-xs text-gray-500 italic">
          {product.description}
        </div>
      )}
      <div className="text-xs text-gray-500">
        Product ID: {productId}
      </div>
    </div>
  );
};

const ProductSummaryDialog: React.FC<ProductSummaryDialogProps> = ({
  order,
  isOpen,
  onClose,
}) => {
  const { translate } = useTranslation();
  const { formatCurrency } = useCurrencyFormat();
  const [suborders, setSuborders] = useState<Order[]>([]);
  const [loadingSuborders, setLoadingSuborders] = useState(false);

  // Fetch suborders if this is a parent order
  useEffect(() => {
    const fetchSuborders = async () => {
      if (!order || (order as any).parentOrderId) {
        setSuborders([]);
        return;
      }

      setLoadingSuborders(true);
      try {
        let orderSuborders: Order[] = [];
        let page = 1;
        const perPage = 1000;
        let hasMore = true;

        while (hasMore) {
          const ordersResponse = await ordersService.getOrders({
            pagination: { page, perPage },
            sort: { field: 'date', order: 'DESC' },
            filter: {},
          });

          const pageSuborders = (ordersResponse.data || []).filter(
            (o: Order) => (o as any).parentOrderId === order.id
          );
          orderSuborders = orderSuborders.concat(pageSuborders);

          if (
            ordersResponse.data.length < perPage ||
            orderSuborders.length >= (ordersResponse.total || 0)
          ) {
            hasMore = false;
          } else {
            page++;
          }
        }

        setSuborders(orderSuborders);
      } catch (error) {
        console.error('Error fetching suborders:', error);
        setSuborders([]);
      } finally {
        setLoadingSuborders(false);
      }
    };

    if (isOpen && order) {
      fetchSuborders();
    }
  }, [isOpen, order]);

  if (!isOpen || !order) return null;

  // Format date
  const orderDate = new Date(order.date);
  const formattedDate = orderDate.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  // Calculate totals
  const itemsTotal = (order.items || []).reduce((sum, item) => {
    const price = typeof item.price === 'number' ? item.price : 0;
    const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
    return sum + price * quantity;
  }, 0);

  const totalExTaxes = order.totalExTaxes || itemsTotal;
  const taxes = order.taxes || 0;
  const deliveryFees = order.deliveryFees || 0;
  const discount = totalExTaxes + taxes + deliveryFees - (order.total || 0);
  const grandTotal = order.total || itemsTotal + taxes + deliveryFees;

  // Calculate suborder totals
  const suborderTotal = suborders.reduce((sum, suborder) => {
    return sum + ((suborder.items || []).reduce((itemSum: number, item: any) => {
      const price = typeof item.price === 'number' ? item.price : 0;
      const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
      return itemSum + price * quantity;
    }, 0));
  }, 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-foreground">
              {translate('pos.orders.modern.productSummary')}
            </h2>
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Package className="h-4 w-4" />
                <span className="font-medium">
                  {translate('pos.orders.fields.reference')}:
                </span>
                <span>{order.reference}</span>
                {(order as any).parentOrderId && (
                  <Badge variant="outline" className="text-xs">
                    {translate('pos.orders.fields.suborder', 'Suborder')}
                  </Badge>
                )}
                {suborders.length > 0 && (
                  <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                    {suborders.length} {translate('pos.orders.fields.suborders', 'Suborders')}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{formattedDate}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Badge
                  className={
                    order.status === 'ordered'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                      : order.status === 'ready'
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                      : order.status === 'delivered'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                  }
                >
                  {translate(`pos.orders.tabs.${order.status}`, order.status)}
                </Badge>
                {order.paid && (
                  <Badge variant="outline" className="text-green-600 dark:text-green-400 border-green-600 dark:border-green-400">
                    {translate('pos.orders.fields.paid')}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {/* Order Information Section */}
          <div className="mb-6 p-4 bg-muted rounded-lg border space-y-3">
            <h3 className="font-semibold text-foreground mb-3">{translate('pos.orders.fields.orderInformation')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{translate('pos.orders.fields.orderType')}:</span>
                <span className="font-medium text-foreground">
                  {order.orderType
                    ? translate(`pos.orders.orderTypes.${order.orderType}`, order.orderType)
                    : 'N/A'}
                </span>
              </div>
              
              {order.tableNumber && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Table:</span>
                  <span className="font-medium text-foreground">{order.tableNumber}</span>
                </div>
              )}
              
              {order.customerId ? (
                <div className="flex items-center gap-2 md:col-span-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Customer:</span>
                  <CustomerReferenceField
                    customerId={order.customerId}
                    showPoints={false}
                    showZalo={false}
                    sourcePlatform={(order as any).sourcePlatform}
                  />
                </div>
              ) : (order as any).sourcePlatform &&
                (order as any).sourcePlatform !== 'internal' ? (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Platform:</span>
                  <Badge variant="outline" className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">
                    {(() => {
                      const platform = (order as any).sourcePlatform?.toLowerCase();
                      const platformMap: Record<string, string> = {
                        grab: 'Grab',
                        shopee: 'Shopee',
                        xanhsm: 'Xanh SM',
                        xanh_sm: 'Xanh SM',
                      };
                      return platformMap[platform] || platform;
                    })()}
                  </Badge>
                </div>
              ) : null}
            </div>
          </div>

          {order.items &&
          Array.isArray(order.items) &&
          order.items.length > 0 ? (
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-4 bg-muted"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">
                        {translate('pos.orders.modern.item')} #{index + 1}
                      </h3>
                      <ProductNameDisplay productId={item.productId} />
                    </div>
                    <div className="text-right ml-4">
                      <div className="font-semibold text-green-600 dark:text-green-400">
                        {formatCurrency(
                          (item.price || 0) * (item.quantity || 0)
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatCurrency(item.price || 0)} × {item.quantity || 0}
                      </div>
                    </div>
                  </div>

                  {item.note && (
                    <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded">
                      <div className="flex items-start">
                        <span className="text-yellow-800 dark:text-yellow-300 text-sm font-medium mr-2">
                          {translate('pos.orders.modern.note')}:
                        </span>
                        <span className="text-yellow-700 dark:text-yellow-300 text-sm">
                          {item.note}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Suborders Section */}
              {suborders.length > 0 && (
                <div className="border-t pt-6 mt-6">
                  <h3 className="font-semibold text-foreground mb-4">
                    {translate('pos.orders.fields.suborders', 'Suborders')} ({suborders.length})
                  </h3>
                  <div className="space-y-3">
                    {suborders.map((suborder) => {
                      const suborderItemsTotal = (suborder.items || []).reduce((sum, item) => {
                        const price = typeof item.price === 'number' ? item.price : 0;
                        const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
                        return sum + price * quantity;
                      }, 0);
                      
                      return (
                        <div key={suborder.id} className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/30">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <div className="font-medium text-foreground">
                                {suborder.reference || `SUB-${suborder.id}`}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {(suborder.items || []).length} {translate('pos.orders.modern.items', 'items')}
                              </div>
                            </div>
                            <Badge
                              className={
                                suborder.status === 'ordered'
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                                  : suborder.status === 'ready'
                                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                                  : suborder.status === 'delivered'
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                              }
                            >
                              {translate(`pos.orders.tabs.${suborder.status}`, suborder.status)}
                            </Badge>
                          </div>
                          {(suborder.items || []).map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm py-1 border-b border-blue-100 dark:border-blue-800 last:border-0">
                              <div className="flex-1">
                                <ProductNameDisplay productId={item.productId} />
                              </div>
                              <div className="text-right ml-4">
                                <span className="font-medium text-foreground">
                                  {formatCurrency((item.price || 0) * (item.quantity || 0))}
                                </span>
                                <span className="text-muted-foreground text-xs ml-2">
                                  ({item.quantity} × {formatCurrency(item.price || 0)})
                                </span>
                              </div>
                            </div>
                          ))}
                          <div className="flex justify-between items-center mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                            <span className="font-medium text-foreground">Subtotal:</span>
                            <span className="font-semibold text-blue-700 dark:text-blue-400">
                              {formatCurrency(suborderItemsTotal)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Order Totals */}
              <div className="border-t pt-6 mt-6 space-y-2">
                <h3 className="font-semibold text-foreground mb-3">
                  {translate('pos.orders.fields.totalBreakdown', 'Total Breakdown')}
                </h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{translate('pos.orders.fields.itemsSubtotal')}:</span>
                    <span className="font-medium text-foreground">{formatCurrency(itemsTotal)}</span>
                  </div>
                  
                  {suborders.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{translate('pos.orders.fields.subordersSubtotal')}:</span>
                      <span className="font-medium text-foreground">{formatCurrency(suborderTotal)}</span>
                    </div>
                  )}
                  
                  {taxes > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{translate('pos.orders.fields.tax')}:</span>
                      <span className="font-medium text-foreground">{formatCurrency(taxes)}</span>
                    </div>
                  )}
                  
                  {deliveryFees > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{translate('pos.orders.fields.deliveryFees')}:</span>
                      <span className="font-medium text-foreground">{formatCurrency(deliveryFees)}</span>
                    </div>
                  )}
                  
                  {discount > 0 && (
                    <div className="flex justify-between text-destructive">
                      <span>Discount:</span>
                      <span className="font-medium">-{formatCurrency(discount)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-lg font-semibold text-foreground">
                      {suborders.length > 0 
                        ? translate('pos.orders.fields.combinedTotal', 'Combined Total')
                        : translate('pos.orders.fields.total', 'Total')}:
                    </span>
                    <span className="text-xl font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(grandTotal + (suborders.length > 0 ? suborderTotal : 0))}
                    </span>
                  </div>
                  
                  {order.paid && (
                    <div className="flex justify-between items-center pt-2 text-green-600 dark:text-green-400">
                      <span className="font-medium">Paid:</span>
                      <span className="font-semibold">{formatCurrency(grandTotal)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {translate('pos.orders.modern.noItems')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t flex-shrink-0">
          <Button onClick={onClose} variant="outline">
            {translate('pos.common.action.close')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductSummaryDialog;
